import { useState, useMemo } from 'react';
import { SortConfig, SortDirection, ColumnConfig } from './types';

export function useSortableData<T>(data: T[], columns: ColumnConfig<T>[], defaultSort?: SortConfig) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(defaultSort || null);

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    const column = columns.find((col) => col.key === sortConfig.key);
    if (!column) return data;

    const type = column.type || 'string';

    const pinnedRows = (data as any[]).filter((row) => row.isAggregate);
    const sortableRows = (data as any[]).filter((row) => !row.isAggregate);

    const sorted = [...sortableRows].sort((a: any, b: any) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Null/undefined values always appear at the bottom of the table
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let comparison = 0;

      if (type === 'number') {
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (Number.isNaN(aNum) && Number.isNaN(bNum)) return 0;
        if (Number.isNaN(aNum)) return 1;
        if (Number.isNaN(bNum)) return -1;
        comparison = aNum - bNum;
      } else if (type === 'date') {
        const parseDate = (v: string) => {
          const ddmmyyyy = v.match(/^(\d{2})\/(\d{2})\/(\d{4})(.*)/);
          return ddmmyyyy
            ? new Date(`${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}${ddmmyyyy[4]}`).getTime()
            : new Date(v).getTime();
        };
        comparison = parseDate(String(aVal)) - parseDate(String(bVal));
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return [...pinnedRows, ...sorted] as T[];
  }, [data, sortConfig, columns]);

  const getSortDirection = (key: string): SortDirection | undefined => {
    if (sortConfig && sortConfig.key === key) return sortConfig.direction;
    return undefined;
  };

  const isColumnSorted = (key: string): boolean => sortConfig?.key === key;

  return { sortedData, sortConfig, requestSort, getSortDirection, isColumnSorted };
}
